import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link2, Upload, FileText, Trash2, Loader2, X, AlertCircle } from 'lucide-react';

/** Esattamente uno fra `uri` e `file`: e' il contratto di POST /best_execution/sources. */
export interface NuovaFonte {
  uri?: string;
  file?: File;
  source_mimetype?: string;
}

interface Props {
  /** Classe documento a cui la fonte verra' collegata, mostrata in intestazione. */
  documentLabel: string;
  onClose: () => void;
  /** Se rifiuta, il messaggio resta nel pannello e il modale non si chiude. */
  onConferma: (fonte: NuovaFonte) => Promise<void>;
}

type Origine = 'uri' | 'file';

const MIME_OPTIONS = [
  { value: '', label: 'Rileva automaticamente' },
  { value: 'application/pdf', label: 'PDF' },
  { value: 'text/html', label: 'Pagina web' },
  { value: 'application/xml', label: 'XML' },
  {
    value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    label: 'Excel',
  },
];

const ACCEPT =
  '.pdf,.html,.htm,.xml,.xlsx,.xls,.csv,application/pdf,text/html,application/xml,text/xml,text/csv,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

const dimensione = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Registrazione di una fonte: URL remoto da scaricare, oppure file caricato.
 *
 * Sostituisce la `Swal.fire` con html grezzo: quella non permetteva di mostrare
 * lo stato di caricamento ne' di tenere aperto il pannello sull'errore del server.
 */
const AddSourceModal: React.FC<Props> = ({ documentLabel, onClose, onConferma }) => {
  const [origine, setOrigine] = useState<Origine>('uri');
  const [uri, setUri] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [mimetype, setMimetype] = useState('');
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [trascinamento, setTrascinamento] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const cambiaOrigine = (nuova: Origine) => {
    setOrigine(nuova);
    setErrore('');
    // L'API accetta esattamente uno dei due: si svuota quello abbandonato per
    // non inviarli entrambi se si cambia idea a meta' strada.
    if (nuova === 'file') setUri('');
    else setFile(null);
  };

  const scegliFile = (selezionati: FileList | null) => {
    const primo = selezionati?.[0];
    if (primo) {
      setFile(primo);
      setErrore('');
    }
  };

  const conferma = async () => {
    if (origine === 'file') {
      if (!file) {
        setErrore('Scegli un file da caricare.');
        return;
      }
    } else {
      const pulito = uri.trim();
      if (!pulito) {
        setErrore('Inserisci l’URL della fonte.');
        return;
      }
      try {
        new URL(pulito);
      } catch {
        setErrore('URL non valido.');
        return;
      }
    }

    setErrore('');
    setInCorso(true);
    try {
      await onConferma({
        uri: origine === 'uri' ? uri.trim() : undefined,
        file: origine === 'file' ? file! : undefined,
        source_mimetype: mimetype || undefined,
      });
    } catch (err) {
      // Il `detail` del 400 e' scritto per l'utente (URL che serve una pagina di
      // login, MIME che non torna, download fallito): va mostrato cosi' com'e'.
      setErrore(err instanceof Error ? err.message : 'Impossibile aggiungere la fonte.');
    } finally {
      setInCorso(false);
    }
  };

  const tabClass = (attiva: boolean) =>
    `flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
      attiva
        ? 'bg-textPrimary text-white'
        : 'bg-white text-textSecondary border border-borderDivider hover:border-textPrimary/40 hover:text-textPrimary'
    }`;

  return (
    <div className="fixed inset-0 bg-modalOverlay flex items-center justify-center z-[70] p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="bg-surface rounded-2xl shadow-xl w-full max-w-md p-5 flex flex-col gap-4"
      >
        {/* Intestazione */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-textPrimary">Aggiungi nuova fonte</h3>
            <p className="text-[11px] text-textMuted truncate mt-0.5">{documentLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={inCorso}
            className="shrink-0 text-textMuted hover:text-textPrimary transition-colors disabled:opacity-40"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scelta dell'origine */}
        <div className="flex gap-2">
          <button type="button" onClick={() => cambiaOrigine('uri')} className={tabClass(origine === 'uri')}>
            <Link2 className="w-3.5 h-3.5" />
            URL remoto
          </button>
          <button type="button" onClick={() => cambiaOrigine('file')} className={tabClass(origine === 'file')}>
            <Upload className="w-3.5 h-3.5" />
            Carica file
          </button>
        </div>

        {origine === 'uri' ? (
          <div className="flex flex-col gap-1.5">
            <input
              type="url"
              value={uri}
              onChange={(e) => {
                setUri(e.target.value);
                if (errore) setErrore('');
              }}
              placeholder="https://esempio.it/documento.pdf"
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-borderDivider text-sm text-textPrimary placeholder:text-textMuted focus:outline-none focus:border-textPrimary/50"
            />
            <p className="text-[11px] text-textMuted">
              Il server scarica il documento e ne conserva una copia. Su file grossi può
              richiedere fino a una trentina di secondi.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                scegliFile(e.target.files);
                e.target.value = '';
              }}
            />

            {file ? (
              <div className="flex items-center gap-3 p-3 rounded-xl border border-green-200 bg-green-50/50">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-textPrimary truncate">{file.name}</div>
                  <div className="text-[11px] text-textMuted">{dimensione(file.size)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  disabled={inCorso}
                  className="shrink-0 text-textMuted hover:text-red-500 transition-colors disabled:opacity-40"
                  title="Rimuovi"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setTrascinamento(true);
                }}
                onDragLeave={() => setTrascinamento(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setTrascinamento(false);
                  scegliFile(e.dataTransfer.files);
                }}
                className={`w-full flex flex-col items-center justify-center gap-1.5 py-6 rounded-xl border border-dashed transition-all ${
                  trascinamento
                    ? 'border-textPrimary bg-textPrimary/5'
                    : 'border-borderDivider hover:border-textPrimary/40'
                }`}
              >
                <Upload className="w-5 h-5 text-textPrimary/50" />
                <span className="text-xs font-medium text-textPrimary">Scegli un file</span>
                <span className="text-[11px] text-textMuted">
                  oppure trascinalo qui — PDF, HTML, XML, Excel, CSV
                </span>
              </button>
            )}
          </div>
        )}

        {/* Hint sul tipo: facoltativo, il server rileva comunque il tipo reale */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-medium text-textSecondary">Tipo documento</label>
          <select
            value={mimetype}
            onChange={(e) => setMimetype(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-borderDivider text-sm text-textPrimary bg-white focus:outline-none focus:border-textPrimary/50"
          >
            {MIME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-textMuted">
            Lascialo su automatico: il server rileva il tipo reale e rifiuta la fonte se
            l’indicazione manuale lo contraddice.
          </p>
        </div>

        {errore && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg border border-red-200 bg-red-50 text-[11px] text-red-800">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span className="break-words">{errore}</span>
          </div>
        )}

        {/* Azioni */}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={inCorso}
            className="px-4 py-2 rounded-lg text-xs font-medium bg-buttonPrimaryBg text-buttonPrimaryText hover:bg-buttonPrimaryHover transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={conferma}
            disabled={inCorso}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-textPrimary text-white hover:bg-textSecondary transition-colors disabled:opacity-50"
          >
            {inCorso && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {inCorso ? 'Aggiunta…' : 'Aggiungi'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AddSourceModal;
